"use client";

import { useMemo, useState } from "react";
import {
  getPhotoEditorFilterCategories,
  getToolsForCategory,
  GO_VIRAL_AVATAR_STYLE_COUNT,
  GO_VIRAL_AVATARS_HREF,
  GO_VIRAL_CATEGORY_ID,
  type PhotoEditorCategory,
} from "@/app/editor/data/ai-photo-editor";
import { getPhotoEditorGoViralAvatarStyles } from "@/components/dashboard/jtbd/jtbdData";
import CatalogAvatarStyleCard from "./CatalogAvatarStyleCard";
import CatalogToolCard from "./CatalogToolCard";

const goViralAvatarStyles = getPhotoEditorGoViralAvatarStyles(GO_VIRAL_AVATAR_STYLE_COUNT);

export default function CatalogToolsGrid({ defaultCategoryId = "popular" }: { defaultCategoryId?: string }) {
  const categories = useMemo(() => getPhotoEditorFilterCategories(), []);
  const [activeId, setActiveId] = useState(defaultCategoryId);

  const active: PhotoEditorCategory | undefined =
    categories.find((c) => c.id === activeId) ?? categories[0];
  const tools = active ? getToolsForCategory(active.id) : [];

  return (
    <div>
      <div className="flex items-end justify-between gap-4 mb-4">
        <div>
          <h2
            className="text-2xl md:text-3xl font-bold text-gray-800"
            style={{ fontFamily: "var(--font-merriweather)" }}
          >
            {active?.title ?? "Tools"}
          </h2>
          {active?.tagline && <p className="text-gray-500 text-sm md:text-base mt-1">{active.tagline}</p>}
        </div>
        {active?.id === GO_VIRAL_CATEGORY_ID ? (
          <a
            href={GO_VIRAL_AVATARS_HREF}
            className="text-primary font-semibold hover:underline whitespace-nowrap text-sm md:text-base"
          >
            Show all →
          </a>
        ) : null}
      </div>

      <div className="relative -mx-4 sm:-mx-6 lg:-mx-8 mb-6">
        <div className="flex gap-2 overflow-x-auto px-4 sm:px-6 lg:px-8 pb-1" style={{ scrollbarWidth: "none" }}>
          {categories.map((category) => {
            const selected = category.id === active?.id;
            return (
              <button
                key={category.id}
                type="button"
                onClick={() => setActiveId(category.id)}
                className={`shrink-0 inline-flex items-center gap-1.5 rounded-full border px-3.5 py-2 text-sm font-semibold whitespace-nowrap transition-colors ${
                  selected
                    ? "border-gray-900 bg-gray-900 text-white"
                    : "border-gray-200 bg-white text-gray-700 hover:border-gray-300"
                }`}
              >
                {category.emoji && <span>{category.emoji}</span>}
                <span>{category.title.replace(/ Tools$/, "")}</span>
              </button>
            );
          })}
        </div>
      </div>

      {active?.id === GO_VIRAL_CATEGORY_ID ? (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-8 md:gap-x-6 md:gap-y-10">
          {goViralAvatarStyles.map((style) => (
            <CatalogAvatarStyleCard key={style.id} style={style} />
          ))}
        </div>
      ) : tools.length === 0 ? (
        <p className="text-sm text-gray-500">No tools in this category yet.</p>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-4 gap-y-8 md:gap-x-6 md:gap-y-10">
          {tools.map((tool) => (
            <CatalogToolCard key={tool.id} tool={tool} />
          ))}
        </div>
      )}
    </div>
  );
}
